<div class="container-fluid">
  <!-- Header -->
  <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
    <h3 class="page-title mb-0" translate>EVENT_STATISTICS</h3>
    <div class="date-controls">
      <select class="date-select" [ngModel]="selectedPreset" (ngModelChange)="onPresetChange($event)">
        <option value="TODAY" translate>TODAY</option>
        <option value="LAST_7_DAYS" translate>LAST_7_DAYS</option>
        <option value="LAST_30_DAYS" translate>LAST_30_DAYS</option>
        <option value="LAST_90_DAYS" translate>LAST_90_DAYS</option>
        <option value="THIS_YEAR" translate>THIS_YEAR</option>
        <option value="" translate>CUSTOM</option>
      </select>
      <input type="date" class="date-input" [value]="fromDateStr" (change)="onFromDateChange($any($event.target).value)">
      <span class="date-separator">&ndash;</span>
      <input type="date" class="date-input" [value]="toDateStr" (change)="onToDateChange($any($event.target).value)">
    </div>
  </div>

  <!-- Tabs -->
  <div class="tab-nav">
    <button class="tab-btn" [class.active]="activeTab === 'overview'" (click)="setTab('overview')" translate>OVERVIEW</button>
    <button class="tab-btn" [class.active]="activeTab === 'products'" (click)="setTab('products')" translate>PRODUCTS_AND_CART</button>
    <button class="tab-btn" [class.active]="activeTab === 'search'" (click)="setTab('search')" translate>SEARCH_AND_FILTERS</button>
    <button class="tab-btn" [class.active]="activeTab === 'users'" (click)="setTab('users')" translate>USERS</button>
    <button class="tab-btn" [class.active]="activeTab === 'journeys'" (click)="setTab('journeys')" translate>JOURNEYS</button>
  </div>

  <ng-container *ngIf="!loading; else pageSkeleton">

    <!-- ═══ Overview Tab ═══ -->
    <ng-container *ngIf="activeTab === 'overview'">
      <div class="kpi-strip">
        <div class="kpi-item" *ngFor="let kpi of overviewKpis">
          <div class="kpi-label" translate>{{ kpi.label }}</div>
          <div class="kpi-value">{{ kpi.value | number }}</div>
        </div>
      </div>

      <div class="panel">
        <div class="panel-header" translate>ACTIVITY_OVER_TIME</div>
        <div class="panel-body">
          <div class="chart-container">
            <canvas baseChart [datasets]="overviewChartData" [labels]="overviewChartLabels" [options]="overviewChartOptions" [type]="'line'"></canvas>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>EVENTS_BY_TYPE</div>
            <div class="panel-body">
              <div class="chart-container" *ngIf="eventsByTypeLabels.length > 0; else emptyState">
                <canvas baseChart [datasets]="eventsByTypeData" [labels]="eventsByTypeLabels" [options]="eventsByTypeOptions" [type]="'bar'"></canvas>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>TRAFFIC_BY_SOURCE</div>
            <div class="panel-body">
              <div class="chart-container" *ngIf="trafficBySourceLabels.length > 0; else emptyState">
                <canvas baseChart [datasets]="trafficBySourceData" [labels]="trafficBySourceLabels" [options]="trafficBySourceOptions" [type]="'bar'"></canvas>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ═══ Products & Cart Tab ═══ -->
    <ng-container *ngIf="activeTab === 'products'">
      <div class="kpi-strip">
        <div class="kpi-item" *ngFor="let kpi of cartKpis">
          <div class="kpi-label" translate>{{ kpi.label }}</div>
          <div class="kpi-value">{{ kpi.value | number }}</div>
        </div>
      </div>

      <div class="panel">
        <div class="panel-header" translate>PRODUCT_VIEWS_VS_CART_ADDS</div>
        <div class="panel-body">
          <div class="chart-container">
            <canvas baseChart [datasets]="conversionData" [labels]="conversionLabels" [options]="conversionOptions" [type]="'line'"></canvas>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>TOP_PRODUCTS</div>
            <div class="panel-body-flush table-scroll">
              <table class="data-table" *ngIf="topProducts.length > 0; else emptyState">
                <thead><tr><th class="col-rank">#</th><th translate>PRODUCT</th><th class="col-value" translate>VIEWS</th></tr></thead>
                <tbody>
                  <tr *ngFor="let item of topProducts; let i = index">
                    <td class="col-rank">{{ i + 1 }}</td>
                    <td class="col-truncate">{{ item.id }}</td>
                    <td class="col-value">{{ item.count | number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>TOP_PAGES</div>
            <div class="panel-body-flush table-scroll">
              <table class="data-table" *ngIf="topPages.length > 0; else emptyState">
                <thead><tr><th class="col-rank">#</th><th translate>PAGE</th><th class="col-value" translate>VIEWS</th></tr></thead>
                <tbody>
                  <tr *ngFor="let item of topPages; let i = index">
                    <td class="col-rank">{{ i + 1 }}</td>
                    <td class="col-truncate">{{ item.id }}</td>
                    <td class="col-value">{{ item.count | number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ═══ Search & Filters Tab ═══ -->
    <ng-container *ngIf="activeTab === 'search'">
      <div class="d-flex align-items-center gap-2 mb-3" *ngIf="availableLists.length > 0">
        <span class="kpi-label mb-0" translate>LIST</span>
        <select class="list-select" [ngModel]="selectedList" (ngModelChange)="onListChange($event)">
          <option value="">{{ 'ALL_LISTS' | translate }}</option>
          <option *ngFor="let list of availableLists" [value]="list">{{ list }}</option>
        </select>
      </div>

      <ng-container *ngIf="!loadingSearch; else searchSkeleton">
        <div class="row">
          <div class="col-12 col-lg-6">
            <div class="panel">
              <div class="panel-header" translate>TOP_SEARCH_TERMS</div>
              <div class="panel-body-flush table-scroll">
                <table class="data-table" *ngIf="topSearchTerms.length > 0; else emptyState">
                  <thead><tr><th class="col-rank">#</th><th translate>TERM</th><th class="col-value" translate>COUNT</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let item of topSearchTerms; let i = index">
                      <td class="col-rank">{{ i + 1 }}</td>
                      <td>{{ item.id }}</td>
                      <td class="col-value">{{ item.count | number }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-6">
            <div class="panel">
              <div class="panel-header" translate>TOP_FILTER_CHOICES</div>
              <div class="panel-body-flush table-scroll">
                <table class="data-table" *ngIf="filterChoices.length > 0; else emptyState">
                  <thead><tr><th class="col-rank">#</th><th translate>FILTER</th><th translate>VALUE</th><th class="col-value" translate>COUNT</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let item of filterChoices; let i = index">
                      <td class="col-rank">{{ i + 1 }}</td>
                      <td>{{ item.filterName }}</td>
                      <td>{{ item.filterValue }}</td>
                      <td class="col-value">{{ item.count | number }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
    </ng-container>

    <!-- ═══ Users Tab ═══ -->
    <ng-container *ngIf="activeTab === 'users'">
      <div class="row">
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>TOP_USERS</div>
            <div class="panel-body-flush table-scroll">
              <table class="data-table" *ngIf="topUsers.length > 0; else emptyState">
                <thead><tr><th class="col-rank">#</th><th translate>USER</th><th class="col-value" translate>EVENTS</th></tr></thead>
                <tbody>
                  <tr *ngFor="let item of topUsers; let i = index">
                    <td class="col-rank">{{ i + 1 }}</td>
                    <td>{{ item.id }}</td>
                    <td class="col-value">{{ item.count | number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-6">
          <div class="panel">
            <div class="panel-header" translate>TOP_CUSTOMERS</div>
            <div class="panel-body-flush table-scroll">
              <table class="data-table" *ngIf="topCustomers.length > 0; else emptyState">
                <thead><tr><th class="col-rank">#</th><th translate>CUSTOMER</th><th class="col-value" translate>EVENTS</th></tr></thead>
                <tbody>
                  <tr *ngFor="let item of topCustomers; let i = index">
                    <td class="col-rank">{{ i + 1 }}</td>
                    <td>{{ item.id }}</td>
                    <td class="col-value">{{ item.count | number }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- ═══ Journeys Tab ═══ -->
    <ng-container *ngIf="activeTab === 'journeys'">
      <!-- Recent Activity (all users) -->
      <div class="panel">
        <div class="panel-header" translate>RECENT_SESSIONS</div>
        <div class="panel-body">
          <ng-container *ngIf="loadingGeneral && generalTimelines.length === 0">
            <div class="skeleton skeleton-row" *ngFor="let _ of [1,2,3,4,5]"></div>
          </ng-container>

          <ng-container *ngIf="generalTimelines.length > 0">
            <ng-container *ngTemplateOutlet="sessionList; context: { $implicit: generalTimelines, showUser: true }"></ng-container>
            <button class="load-more-btn" *ngIf="canLoadMoreGeneral" (click)="loadMoreGeneral()" [disabled]="loadingGeneral" translate>LOAD_MORE</button>
          </ng-container>

          <div class="empty-state" *ngIf="!loadingGeneral && generalTimelines.length === 0" translate>NO_DATA</div>
        </div>
      </div>

      <!-- User-specific lookup -->
      <div class="panel">
        <div class="panel-header" translate>USER_SESSION_LOOKUP</div>
        <div class="panel-body">
          <div class="d-flex gap-2 align-items-center mb-3">
            <input type="text" class="date-input" style="width: 260px"
                   [placeholder]="'ENTER_USER_ID' | translate"
                   [(ngModel)]="timelineUserId"
                   (keyup.enter)="lookupTimeline()">
            <button class="lookup-btn" (click)="lookupTimeline()" [disabled]="!timelineUserId.trim()" translate>LOOKUP</button>
          </div>

          <ng-container *ngIf="loadingTimeline">
            <div class="skeleton skeleton-row" *ngFor="let _ of [1,2,3]"></div>
          </ng-container>

          <ng-container *ngIf="!loadingTimeline && sessionTimelines.length > 0">
            <ng-container *ngTemplateOutlet="sessionList; context: { $implicit: sessionTimelines, showUser: false }"></ng-container>
            <button class="load-more-btn" *ngIf="canLoadMore" (click)="loadMoreTimelines()" [disabled]="loadingTimeline" translate>LOAD_MORE</button>
          </ng-container>

          <div class="empty-state" *ngIf="!loadingTimeline && sessionTimelines.length === 0 && timelineUserId" translate>NO_SESSIONS_FOUND</div>
        </div>
      </div>
    </ng-container>

  </ng-container>
</div>

<!-- Skeletons -->
<ng-template #pageSkeleton>
  <div class="kpi-strip">
    <div class="kpi-item" *ngFor="let _ of [1,2,3,4]">
      <div class="skeleton skeleton-kpi"></div>
    </div>
  </div>
  <div class="panel"><div class="panel-body"><div class="skeleton skeleton-chart"></div></div></div>
</ng-template>

<ng-template #searchSkeleton>
  <div class="row">
    <div class="col-12 col-lg-6">
      <div class="panel"><div class="panel-body">
        <div class="skeleton skeleton-row" *ngFor="let _ of [1,2,3,4,5]"></div>
      </div></div>
    </div>
    <div class="col-12 col-lg-6">
      <div class="panel"><div class="panel-body">
        <div class="skeleton skeleton-row" *ngFor="let _ of [1,2,3,4,5]"></div>
      </div></div>
    </div>
  </div>
</ng-template>

<!-- Shared session timeline template -->
<ng-template #sessionList let-sessions let-showUser="showUser">
  <div class="timeline-session" *ngFor="let session of sessions">
    <div class="timeline-session-header">
      <span class="timeline-session-id">{{ session.sessionId | slice:0:8 }}...</span>
      <span class="timeline-meta" *ngIf="showUser && session.userId">{{ session.userId }}</span>
      <span class="timeline-meta">{{ session.startedAt | date:'short' }}</span>
      <span class="timeline-meta" *ngIf="session.events.length > 0">&ndash; {{ session.events[session.events.length - 1].occuredAt | date:'HH:mm:ss' }}</span>
      <span class="timeline-meta timeline-event-count">{{ session.events.length }} {{ 'EVENTS' | translate }}</span>
    </div>
    <div class="timeline-events">
      <div class="timeline-event" *ngFor="let evt of session.events; let last = last">
        <div class="timeline-dot" [ngClass]="'dot-' + evt.eventType"></div>
        <div class="timeline-connector" *ngIf="!last"></div>
        <div class="timeline-event-content">
          <span class="event-badge">{{ evt.eventType }}</span>
          <span class="timeline-customer" *ngIf="evt.customerId">{{ evt.customerId }}</span>
          <span class="timeline-detail" *ngIf="evt.detail">{{ evt.detail }}</span>
          <span class="timeline-time">{{ evt.occuredAt | date:'HH:mm:ss' }}</span>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #emptyState>
  <div class="empty-state" translate>NO_DATA</div>
</ng-template>
